import { useCallback, useMemo, useState } from 'react';

import { getUserType, useAuthentication } from '@ravago/shared/authentication';
import { ProductDetail } from '@ravago/shared/page-data/models/elements';

import { parseAsString, useQueryState, useQueryStates } from 'nuqs';

import { TrackingUtils } from '../../../../utils/tracking/tracking.utils';
import { RfqColorSelectorValue } from '../components/request-for-quotation/rfq-color-selector';
import { DEFAULT_PRODUCER_COLOR_CODE } from '../constants';
import { getColorNameFromId } from '../utils/color-utils';
import { parseAsColor, parseAsFlyoutState } from '../utils/parsers';
import { resolveRfqUrlSelection } from '../utils/product-action-url-state';

interface Props {
   productDetail: ProductDetail | undefined;
   initialColor?: RfqColorSelectorValue;
}

interface UrlActionDetails {
   from: string | null;
   colorId: string | null;
   colorCode: string | null;
   color: string | null;
}

const createUrlSearchParams = ({ from, colorId, colorCode, color }: UrlActionDetails) => {
   const searchParams = new URLSearchParams();

   if (from) searchParams.set('from', from);
   if (colorId) searchParams.set('colorId', colorId);
   if (colorCode) searchParams.set('colorCode', colorCode);
   if (color) searchParams.set('color', color);
   return searchParams;
};

const getUrlRfqColor = (urlActionDetails: UrlActionDetails): RfqColorSelectorValue | undefined => {
   const searchParams = createUrlSearchParams(urlActionDetails);
   const rfqUrlSelection = resolveRfqUrlSelection(searchParams);
   if (!rfqUrlSelection) return undefined;

   return {
      colorId: rfqUrlSelection.colorId,
      colorName: rfqUrlSelection.colorName,
      producerColorCode: rfqUrlSelection.colorCode,
      desiredColor: undefined,
      projectDescription: undefined
   };
};

const addRfqColorToProductDetail = (
   productDetail: ProductDetail | undefined,
   urlRfqColor: RfqColorSelectorValue | undefined
): ProductDetail | undefined => {
   if (!productDetail || !urlRfqColor?.colorId || !urlRfqColor.colorName) return productDetail;

   const existingColor = productDetail.colors.find((color) => color.id === urlRfqColor.colorId);
   const producerColorCode = urlRfqColor.producerColorCode;

   if (!existingColor) {
      return {
         ...productDetail,
         colors: [
            ...productDetail.colors,
            {
               id: urlRfqColor.colorId,
               name: urlRfqColor.colorName,
               defaultColor: false,
               producerColorCodes: producerColorCode && producerColorCode !== DEFAULT_PRODUCER_COLOR_CODE ? [{ colorCode: producerColorCode }] : []
            }
         ]
      };
   }

   if (
      producerColorCode &&
      producerColorCode !== DEFAULT_PRODUCER_COLOR_CODE &&
      !existingColor.producerColorCodes.some(({ colorCode }) => colorCode.toLowerCase() === producerColorCode.toLowerCase())
   ) {
      return {
         ...productDetail,
         colors: productDetail.colors.map((color) =>
            color.id === urlRfqColor.colorId ? { ...color, producerColorCodes: [...color.producerColorCodes, { colorCode: producerColorCode }] } : color
         )
      };
   }

   return productDetail;
};

export const useRequestForQuotationState = ({ productDetail, initialColor }: Props) => {
   const { user } = useAuthentication();
   const [urlActionDetails] = useQueryStates({
      from: parseAsString,
      colorId: parseAsString,
      colorCode: parseAsString,
      color: parseAsString
   });

   const urlRfqColor = useMemo(() => getUrlRfqColor(urlActionDetails), [urlActionDetails]);
   const productDetailForRfq = useMemo(() => addRfqColorToProductDetail(productDetail, urlRfqColor), [productDetail, urlRfqColor]);

   const colors = productDetailForRfq?.colors ?? [];
   const [rfqColorDetails, setRfqColorDetails] = useQueryStates({
      color: parseAsColor(colors),
      producerColorCode: parseAsString,
      desiredColor: parseAsString
   });
   const [projectDescription, setProjectDescription] = useState<string | undefined>(undefined);

   const [flyoutOpen, setFlyoutOpen] = useQueryState('flyout', parseAsFlyoutState.withDefault(false));

   const setValue = useCallback((newValue: RfqColorSelectorValue) => {
      setRfqColorDetails({
         color: newValue.colorId,
         producerColorCode: newValue.producerColorCode || null,
         desiredColor: newValue.desiredColor || null
      });
      setProjectDescription(newValue.projectDescription);
   }, [setRfqColorDetails]);

   const resolvedInitialColor = urlRfqColor ?? initialColor;
   const resolvedColorId = rfqColorDetails.color ?? resolvedInitialColor?.colorId ?? undefined;
   const resolvedFromUrlRfqColor = resolvedColorId !== undefined && resolvedColorId === urlRfqColor?.colorId;
   const urlColorNameFallback = resolvedFromUrlRfqColor ? urlRfqColor?.colorName : undefined;
   const resolvedColorName = resolvedColorId
      ? (getColorNameFromId(colors, resolvedColorId) ?? urlColorNameFallback ?? resolvedInitialColor?.colorName ?? '')
      : '';
   const resolvedProducerColorCode =
      rfqColorDetails.producerColorCode || (resolvedFromUrlRfqColor ? urlRfqColor?.producerColorCode : undefined) || resolvedInitialColor?.producerColorCode || undefined;
   const value = {
      colorId: resolvedColorId,
      colorName: resolvedColorName,
      producerColorCode: resolvedProducerColorCode,
      desiredColor: rfqColorDetails.desiredColor ?? undefined,
      projectDescription
   };

   const setFlyoutOpenValue = useCallback(
      (open: boolean) => {
         if (open) {
            if (!productDetailForRfq) return;
            setFlyoutOpen(true);
            TrackingUtils.track('rfq_click_start_quote_request', {
               productName: productDetailForRfq.description,
               productId: productDetailForRfq.id,
               userLoggedIn: JSON.stringify(!!user),
               userType: getUserType(user)
            });
         } else {
            setFlyoutOpen(false);
         }
      },
      [productDetailForRfq, setFlyoutOpen, user]
   );

   const clearValue = useCallback(() => {
      setRfqColorDetails(null);
      setProjectDescription(undefined);
   }, [setRfqColorDetails]);

   return {
      value,
      productDetail: productDetailForRfq,
      flyoutOpen,
      setValue,
      clearValue,
      openFlyout: setFlyoutOpenValue
   };
};
